import { PhotoCrop, api } from '@/api';
import { Baul, BaulRole, Persona, PersonaInvite, PersonaRelationship, PersonaSpouseRelationship, Photo } from '@/types';
import { sharePublicLink } from '@/features/sharing/sharePublicLink';
import { usePersonasStore } from '@/store/usePersonasStore';
import { usePhotosStore } from '@/store/usePhotosStore';
import { withOptimisticUpdate } from '@/store/withOptimisticUpdate';

// Its callers are BaulPersonasTabContainer (features/people/containers) and
// InvitarFamiliaRoute (features/sharing/routes) — moved here from features/baules/useCases
// when the former took over the baúl's Personas tab. Returns the created Persona so
// InvitarFamiliaRoute can immediately start its invite flow without a second round trip.
export async function createPersona(baulId: string, nickname: string, role?: BaulRole): Promise<Persona> {
  const persona = await api.baules.createPersona(baulId, nickname, role);
  usePersonasStore.setState((state) => ({
    personas: { ...state.personas, [baulId]: [...(state.personas[baulId] || []), persona] },
  }));
  return persona;
}

// Issues (or re-shares) a persona's 1:1 invite link — shared between "Invitar a la familia"
// (InvitarFamiliaRoute) and the "Enviar invitación" action on the persona ficha
// (PersonaSettingsMenuContainer), so both go through the exact same invite + share flow.
export async function sharePersonaInvite(
  baul: Pick<Baul, 'id' | 'name'>,
  persona: Pick<Persona, 'id' | 'nickname'>,
  onCopied: () => void
): Promise<PersonaInvite> {
  const invite = await api.baules.invitePersona(baul.id, persona.id);
  await sharePublicLink({
    title: `Invitación a ${baul.name}`,
    text: `Te invito a unirte a mi baúl de recuerdos "${baul.name}" en El Baúl, ${persona.nickname}.`,
    url: invite.url,
    onCopied,
  });
  return invite;
}

export async function loadPersonas(baulId: string): Promise<void> {
  const personas = await api.baules.getPersonas(baulId);
  usePersonasStore.setState((state) => ({ personas: { ...state.personas, [baulId]: personas } }));
}

export async function loadPersonaPhotos(baulId: string, personaId: string): Promise<void> {
  const photos = await api.baules.getPersonaPhotos(baulId, personaId);
  usePhotosStore.getState().upsertPhotos(photos);
  usePersonasStore.setState((state) => ({
    personaPhotos: { ...state.personaPhotos, [personaId]: photos.map((photo) => photo.id) },
  }));
}

export async function updatePersona(baulId: string, personaId: string, name: string, nickname: string): Promise<void> {
  const updated = await api.baules.updatePersona(baulId, personaId, name, nickname);
  usePersonasStore.setState((state) => ({
    personas: {
      ...state.personas,
      [baulId]: (state.personas[baulId] || []).map((u) => (u.id === personaId ? updated : u)),
    },
  }));
}

export async function updatePersonaBiografia(baulId: string, personaId: string, biografia: string): Promise<void> {
  const updated = await api.baules.updatePersonaBiografia(baulId, personaId, biografia);
  usePersonasStore.setState((state) => ({
    personas: {
      ...state.personas,
      [baulId]: (state.personas[baulId] || []).map((u) => (u.id === personaId ? updated : u)),
    },
  }));
}

export async function uploadPersonaAvatar(baulId: string, personaId: string, file: File, crop: PhotoCrop): Promise<void> {
  const updated = await api.baules.uploadPersonaAvatar(baulId, personaId, file, crop);
  usePersonasStore.setState((state) => ({
    personas: {
      ...state.personas,
      [baulId]: (state.personas[baulId] || []).map((u) => (u.id === personaId ? updated : u)),
    },
  }));
  loadPersonaPhotos(baulId, personaId).catch(() => undefined);
}

export async function setPersonaAvatarPhoto(baulId: string, personaId: string, photo: Photo, crop: PhotoCrop): Promise<void> {
  const updated = await api.baules.setPersonaAvatarPhoto(baulId, personaId, photo.id, crop);
  usePhotosStore.getState().upsertPhotos([photo]);
  usePersonasStore.setState((state) => {
    const currentPhotoIds = state.personaPhotos[personaId] || [];
    const hasPhoto = currentPhotoIds.includes(photo.id);
    return {
      personas: {
        ...state.personas,
        [baulId]: (state.personas[baulId] || []).map((u) => (u.id === personaId ? updated : u)),
      },
      personaPhotos: hasPhoto
        ? state.personaPhotos
        : { ...state.personaPhotos, [personaId]: [photo.id, ...currentPhotoIds] },
    };
  });
}

// Optimista: el <select> de rol está controlado por este valor, así que sin aplicar
// el cambio antes del await se ve "rebotar" al valor anterior mientras se espera al
// servidor. Si la petición falla, se revierte al snapshot previo (ver withOptimisticUpdate
// para el detalle de snapshot/apply/rollback compartido).
export async function updateUserRole(baulId: string, personaId: string, role: BaulRole): Promise<void> {
  await withOptimisticUpdate({
    getSnapshot: () => usePersonasStore.getState().personas[baulId] || [],
    applyOptimistic: () => usePersonasStore.setState((state) => ({
      personas: {
        ...state.personas,
        [baulId]: (state.personas[baulId] || []).map((u) => (u.id === personaId ? { ...u, role } : u)),
      },
    })),
    rollback: (previous) => usePersonasStore.setState((state) => ({ personas: { ...state.personas, [baulId]: previous } })),
    operation: () => api.baules.updatePersonaRole(baulId, personaId, role),
  });
}

export async function loadPersonaRelationships(baulId: string): Promise<void> {
  const relationships = await api.baules.getPersonaRelationships(baulId);
  usePersonasStore.setState((state) => ({ relationships: { ...state.relationships, [baulId]: relationships } }));
}

// "Editar relaciones" — creates the single Parent->Child edge; callable from either end (the
// caller has already resolved which of the two ids is the parent, see EditRelationshipsModal),
// see PersonaRelationship's doc comment for why there's no separate "child of" creation path.
export async function addPersonaRelationship(baulId: string, parentId: string, childId: string): Promise<PersonaRelationship> {
  const relationship = await api.baules.addPersonaRelationship(baulId, parentId, childId);
  usePersonasStore.setState((state) => ({
    relationships: { ...state.relationships, [baulId]: [...(state.relationships[baulId] || []), relationship] },
  }));
  return relationship;
}

// Removable from either end — there's only one relationship between two personas, so the pair
// order passed here doesn't need to match how it was originally stored (see
// PersonaRelationshipsController.Remove, api/).
export async function removePersonaRelationship(baulId: string, parentId: string, childId: string): Promise<void> {
  await api.baules.removePersonaRelationship(baulId, parentId, childId);
  usePersonasStore.setState((state) => ({
    relationships: {
      ...state.relationships,
      [baulId]: (state.relationships[baulId] || []).filter(
        (r) => !(r.parentId === parentId && r.childId === childId) && !(r.parentId === childId && r.childId === parentId)
      ),
    },
  }));
}

export async function loadPersonaSpouseRelationships(baulId: string): Promise<void> {
  const spouseRelationships = await api.baules.getPersonaSpouseRelationships(baulId);
  usePersonasStore.setState((state) => ({ spouseRelationships: { ...state.spouseRelationships, [baulId]: spouseRelationships } }));
}

// "Editar relaciones" — creates the single symmetric spouse edge; callable with the two ids in
// either order, see PersonaSpouseRelationship's doc comment. El Baúl models monogamous families
// only: the backend rejects this if either persona already has a spouse.
export async function addPersonaSpouseRelationship(baulId: string, personaId1: string, personaId2: string): Promise<PersonaSpouseRelationship> {
  const relationship = await api.baules.addPersonaSpouseRelationship(baulId, personaId1, personaId2);
  usePersonasStore.setState((state) => ({
    spouseRelationships: { ...state.spouseRelationships, [baulId]: [...(state.spouseRelationships[baulId] || []), relationship] },
  }));
  return relationship;
}

// Removable from either end — there's only one spouse relationship between two personas, so the
// pair order passed here doesn't need to match how it was originally stored (see
// PersonaSpouseRelationshipsController.Remove, api/).
export async function removePersonaSpouseRelationship(baulId: string, personaId1: string, personaId2: string): Promise<void> {
  await api.baules.removePersonaSpouseRelationship(baulId, personaId1, personaId2);
  usePersonasStore.setState((state) => ({
    spouseRelationships: {
      ...state.spouseRelationships,
      [baulId]: (state.spouseRelationships[baulId] || []).filter(
        (r) => !(r.personaId1 === personaId1 && r.personaId2 === personaId2) && !(r.personaId1 === personaId2 && r.personaId2 === personaId1)
      ),
    },
  }));
}

// "Revocar acceso" — clears the account link (and its invite token, server-side) and sets the
// persona's role to sin_acceso, so a revoked member ends up in exactly the state an admin
// would get by picking "Sin acceso" directly. The row falls back to Pending and needs a
// different role picked before it can be invited again (see Persona.RevokeAccess).
export async function revokeAccess(baulId: string, personaId: string): Promise<void> {
  await api.baules.revokeAccess(baulId, personaId);
  usePersonasStore.setState((state) => ({
    personas: {
      ...state.personas,
      [baulId]: (state.personas[baulId] || []).map((persona) =>
        persona.id === personaId
          ? { ...persona, email: undefined, status: 'pending', role: 'sin_acceso' }
          : persona
      ),
    },
  }));
}
